/**
 * iter845d — Lightweight in-memory breadcrumb buffer for the POS.
 *
 * Records the exact sequence of checkout / print / bill-delivery steps
 * that ran during the current session. When a cashier reports "the SAR
 * didn't add up on Order #123" or "the receipt didn't print", the
 * breadcrumb log can be inspected via a hidden POS Settings action or
 * exposed via `window.__smartoPosBreadcrumbs()` in the browser console.
 *
 * Design goals:
 *   • Zero UI weight — pure lib module, no React state.
 *   • Rolling buffer of 200 entries (about 5-10 orders' worth of steps).
 *   • Best-effort persistence to sessionStorage so a page refresh
 *     during triage still surfaces the last-session's tail.
 *   • Never throws — private mode / quota errors are silently swallowed.
 *   • Auto-console.debug in DEV so devs can spot flows in real time
 *     without opening the buffer explicitly.
 */
import { getBootMeta } from '@/lib/buildMeta';


const BUF_KEY = 'smarto_pos_breadcrumbs_v1';
const MAX = 200;

let _buffer = [];
try {
  const raw = typeof window !== 'undefined' ? window.sessionStorage.getItem(BUF_KEY) : null;
  if (raw) _buffer = JSON.parse(raw).slice(-MAX);
} catch { /* private mode / stale — start fresh */ }

const _persist = () => {
  try {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(BUF_KEY, JSON.stringify(_buffer));
    }
  } catch { /* quota exceeded — skip */ }
};

/**
 * Record a breadcrumb.
 * @param {string} step — short kebab-case event name, e.g. 'cart-validated'
 * @param {object} [payload] — small JSON-serialisable context.
 */
export function crumb(step, payload = null) {
  const entry = {
    ts: Date.now(),
    iso: new Date().toISOString(),
    step,
    ...(payload ? { ctx: payload } : {}),
  };
  _buffer.push(entry);
  if (_buffer.length > MAX) _buffer.splice(0, _buffer.length - MAX);
  _persist();
  if (typeof console !== 'undefined' && process.env.NODE_ENV !== 'production') {
    console.debug('[smarto/pos] ', step, payload || '');
  }
}

/** Return a snapshot of all currently-stored breadcrumbs. */
export function getBreadcrumbs() {
  return [..._buffer];
}

/** Wipe the buffer (useful after successful shift close-out). */
export function clearBreadcrumbs() {
  _buffer = [];
  try {
    if (typeof window !== 'undefined') window.sessionStorage.removeItem(BUF_KEY);
  } catch { /* ignore */ }
}

/**
 * Format the buffer as a plain-text report the cashier can copy-paste
 * or paste into a support ticket.
 */
/**
 * Minimal runtime fingerprint prepended to the diagnostic log so support can
 * tell EXACTLY which frontend build / runtime a device is on — distinguishes a
 * stale APK WebView from a true code issue. Pure, sync, best-effort: every
 * lookup is guarded so it can never throw on an old WebView.
 */
export function runtimeDiagnosticHeader() {
  const g = (fn, dflt = 'n/a') => { try { const v = fn(); return (v === undefined || v === null) ? dflt : v; } catch { return dflt; } };
  const cap = g(() => window.Capacitor, null);
  const native = g(() => !!(cap && (cap.isNativePlatform ? cap.isNativePlatform() : cap.isNative)), false);
  const platform = g(() => (cap && cap.getPlatform ? cap.getPlatform() : 'web'), 'web');
  const smartoPrinter = g(() => !!(cap && cap.Plugins && cap.Plugins.SmartoPrinter), false);
  const swCtrl = g(() => (navigator.serviceWorker && navigator.serviceWorker.controller ? navigator.serviceWorker.controller.scriptURL : 'none'), 'none');
  // Build/version from version.json (loaded by buildMeta at startup).
  let build = 'n/a';
  build = g(() => {
    const m = getBootMeta();
    return m ? `${m.commit || '?'} · ${m.built_at || '?'} · ${m.mode || '?'}` : 'n/a';
  }, 'n/a');
  const lines = [
    '=== SMARTO POS RUNTIME ===',
    `frontend build : ${build}`,
    `url/hostname   : ${g(() => window.location.href)}  (host ${g(() => window.location.hostname)})`,
    `capacitor      : native=${native ? 'YES' : 'NO'} platform=${platform}`,
    `smartoPrinter  : ${smartoPrinter ? 'YES' : 'NO'}`,
    `service worker : controller=${swCtrl}`,
    `userAgent      : ${g(() => navigator.userAgent)}`,
    '==========================',
  ];
  return lines.join('\n');
}

export function formatBreadcrumbsAsText() {
  const header = runtimeDiagnosticHeader();
  if (!_buffer.length) return `${header}\n(no POS breadcrumbs recorded this session)`;
  const steps = _buffer.map((e) => {
    const t = e.iso.split('T')[1].replace('Z', '');
    const ctx = e.ctx ? ' ' + JSON.stringify(e.ctx) : '';
    return `${t}  ${e.step}${ctx}`;
  }).join('\n');
  return `${header}\n${steps}`;
}

// Expose a friendly console handle so a cashier / support engineer can
// paste `__smartoPosBreadcrumbs()` in DevTools during a live triage
// without needing to remember the module path.
if (typeof window !== 'undefined') {
  window.__smartoPosBreadcrumbs = () => {
    const txt = formatBreadcrumbsAsText();
    console.log(txt);
    return txt;
  };
}

/**
 * iter845e — Fire-and-forget: POST the current breadcrumb buffer to the
 * backend so a manager sees this failure without waiting for the
 * cashier to escalate. Called from `usePosCheckout` on `checkout-fail`
 * only — never on happy-path orders — to keep bandwidth minimal.
 *
 * Non-blocking, best-effort: any network / auth failure is swallowed so
 * a telemetry hiccup can never make a bad situation worse for the
 * cashier standing in front of a customer.
 */
export async function sendFailureTelemetry({ api, summary, error, branch_id } = {}) {
  if (!api || typeof api.post !== 'function') return { ok: false, reason: 'no-api' };
  try {
    const { data } = await api.post('/pos/telemetry', {
      event: 'checkout-fail',
      summary: (summary || '').toString().slice(0, 500),
      error: (error || '').toString().slice(0, 2000),
      branch_id: branch_id || null,
      breadcrumbs: getBreadcrumbs(),
    });
    return { ok: true, id: data?.id };
  } catch (e) {
    // Do not surface — telemetry must never break the UI.
    return { ok: false, reason: 'network', err: String(e?.message || e) };
  }
}
